import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Inbox } from "lucide-react";
import { adminLogin, adminLogout, getMe, getLeads, updateLeadStatus, formatApiError } from "@/lib/api";

const TYPE_TABS = [
  { key: "", label: "Toutes" },
  { key: "recherche", label: "Recherche privée" },
  { key: "financement", label: "Financement" },
  { key: "conseiller", label: "Conseiller" },
  { key: "flotte", label: "Flotte" },
];

const TYPE_LABELS = {
  recherche: "Recherche privée",
  financement: "Financement",
  conseiller: "Conseiller",
  flotte: "Flotte",
};

const STATUS_OPTIONS = [
  { key: "nouveau", label: "Nouveau" },
  { key: "en_cours", label: "En cours" },
  { key: "complete", label: "Complété" },
];

const STATUS_STYLES = {
  nouveau: "bg-gold/15 text-gold-light border-gold/40",
  en_cours: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  complete: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminLogin(email, password);
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md card-lux !p-10"
      >
        <span className="w-12 h-12 rounded-full border border-gold/50 flex items-center justify-center font-display text-gold text-xl mb-6">
          G
        </span>
        <h1 className="font-display text-3xl text-white mb-2">Espace admin</h1>
        <p className="text-sm text-slate-400 font-light mb-8">
          Gestion Automobile Exécutive Inc. — accès réservé.
        </p>
        <label className="field-label" htmlFor="admin-email">Courriel</label>
        <input
          id="admin-email"
          data-testid="admin-login-email"
          type="email"
          className="field mb-5"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <label className="field-label" htmlFor="admin-password">Mot de passe</label>
        <input
          id="admin-password"
          data-testid="admin-login-password"
          type="password"
          className="field mb-6"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && (
          <p data-testid="admin-login-error" className="text-sm text-red-400 mb-4">{error}</p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="btn-gold w-full disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Se connecter
        </button>
      </form>
    </div>
  );
}

function LeadCard({ lead, onStatus }) {
  const [updating, setUpdating] = useState(false);
  const changeStatus = async (status) => {
    setUpdating(true);
    try {
      await updateLeadStatus(lead.id, status);
      onStatus(lead.id, status);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <article data-testid={`lead-card-${lead.id}`} className="card-lux !p-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="text-[10px] uppercase tracking-[0.2em] bg-white/5 border border-white/15 rounded-full px-3 py-1 text-slate-300">
          {TYPE_LABELS[lead.type] || lead.type}
        </span>
        <span className={`text-[10px] uppercase tracking-[0.2em] border rounded-full px-3 py-1 ${STATUS_STYLES[lead.status] || STATUS_STYLES.nouveau}`}>
          {STATUS_OPTIONS.find((s) => s.key === lead.status)?.label || lead.status}
        </span>
        <span className="ml-auto text-xs text-slate-500 font-mono">
          {lead.created_at ? new Date(lead.created_at).toLocaleString("fr-CA") : ""}
        </span>
      </div>
      <h3 className="text-lg text-white font-medium">{lead.name}</h3>
      <p className="text-sm text-slate-400 mt-1">
        <a href={`tel:${lead.phone}`} className="hover:text-gold-light transition-colors">{lead.phone}</a>
        {" · "}
        <a href={`mailto:${lead.email}`} className="hover:text-gold-light transition-colors">{lead.email}</a>
      </p>
      {lead.details && Object.keys(lead.details).length > 0 && (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 border-t border-white/[0.07] pt-4">
          {Object.entries(lead.details).map(([k, v]) => (
            <div key={k}>
              <dt className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
              <dd className="text-sm text-slate-300 font-light mt-0.5 whitespace-pre-wrap">{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-5 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Statut</label>
        <select
          data-testid={`lead-status-select-${lead.id}`}
          className="field !w-auto !py-2 !px-3 !text-xs"
          value={lead.status}
          disabled={updating}
          onChange={(e) => changeStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.key} value={s.key} className="bg-anthracite">{s.label}</option>
          ))}
        </select>
      </div>
    </article>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const { data } = await getLeads(type);
      setLeads(data.leads);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getMe()
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) load(tab);
  }, [user, tab, load]);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gold" />
      </div>
    );
  }

  if (!user) return <LoginCard onLogin={setUser} />;

  const logout = async () => {
    await adminLogout().catch(() => {});
    setUser(null);
  };

  const onStatus = (id, status) =>
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink">
      <header className="border-b border-white/10 bg-anthracite/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full border border-gold/50 flex items-center justify-center font-display text-gold">G</span>
            <span className="text-sm text-white font-medium">Demandes reçues</span>
            <span data-testid="admin-leads-count" className="font-mono text-xs text-gold-light bg-gold/10 border border-gold/30 rounded-full px-3 py-1">
              {leads.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(tab)}
              className="p-2 text-slate-400 hover:text-gold-light transition-colors"
              aria-label="Rafraîchir"
            >
              <RefreshCw size={16} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-400 hover:text-white transition-colors"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filter-tabs">
          {TYPE_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key || "toutes"}`}
              onClick={() => setTab(t.key)}
              className={`text-[11px] uppercase tracking-[0.18em] rounded-full px-4 py-2 border transition-colors ${
                tab === t.key
                  ? "border-gold/60 text-gold-light bg-gold/10"
                  : "border-white/15 text-slate-400 hover:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={26} className="animate-spin text-gold" />
          </div>
        ) : leads.length === 0 ? (
          <div data-testid="admin-empty-state" className="flex flex-col items-center py-24 text-center">
            <Inbox size={40} className="text-slate-600 mb-4" strokeWidth={1.25} />
            <p className="text-slate-400 font-light">Aucune demande pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-leads-list">
            {leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} onStatus={onStatus} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
